import { useCallback, useMemo, useState } from 'react';
import { Play, Check, SkipForward, Clock, Pause, Tag, X } from 'lucide-react';
import '../../../shared/components/layout/StoreDashboardLayout.css';
import Skeleton from '../../../shared/components/ui/Skeleton';
import { PROFILE_STORES_OFFERS } from '../../../shared/constants/endpoints';

const SECTION_BADGES = {
  BILLING_SECTION: { label: 'BILLING', tone: 'billing' },
  TRIAL_SECTION: { label: 'TRIAL ROOMS', tone: 'trial' },
  EXCHANGE_SECTION: { label: 'EXCHANGE', tone: 'exchange' }
};
const QUEUE_SORT_COLUMNS = [
  { key: 'token', label: 'Token' },
  { key: 'customerName', label: 'Customer' },
  { key: 'waited', label: 'Wait' },
  { key: 'statusLabel', label: 'Status' }
];
const DEFAULT_SORT_CONFIG = { key: null, direction: 'asc' };
const WAIT_AMBER_THRESHOLD_MIN = 10;
const WAIT_RED_THRESHOLD_MIN = 20;

// Vertical is determined from clientType (explicit merchant identity set at registration),
// with module flags as fallback for stores that predate the clientType field.
// clientType values from the backend: RETAIL, CLINIC, SALON, FOOD_COURT
const detectVertical = (clientType, modules) => {
  const type = String(clientType || '').toUpperCase().trim();
  if (type === 'SALON') return 'salon';
  if (type === 'CLINIC') return 'clinic';
  if (type === 'FOOD_COURT') return 'food-court';
  if (type === 'RETAIL') return 'retail';
  // Fallback: infer from module flags for stores without clientType set
  if (modules?.appointments && modules?.offers) return 'salon';
  if (modules?.appointments) return 'clinic';
  if (modules?.offers) return 'food-court';
  return 'retail';
};

const VERTICAL_CONFIG = {
  clinic: {
    heroEyebrow: 'Clinical Operations',
    heroTitle: 'Today\'s Appointments',
    heroSubtitle: 'Track patient bookings, walk-ins, and service flow in real time.',
    heroTags: ['Appointment tracking', 'Patient flow'],
    primaryKpiLabel: 'Appointments today',
    showAppointmentsRail: true,
    showOffersRail: false
  },
  salon: {
    heroEyebrow: 'Salon Operations',
    heroTitle: 'Today\'s Schedule',
    heroSubtitle: 'Manage appointments, active offers, and walk-in queue all in one view.',
    heroTags: ['Appointment tracking', 'Active offers', 'Walk-in queue'],
    primaryKpiLabel: 'Appointments today',
    showAppointmentsRail: true,
    showOffersRail: true
  },
  retail: {
    heroEyebrow: 'Live Operations',
    heroTitle: 'Monitoring Center',
    heroSubtitle: 'Track active counters, waiting pressure, and service velocity in real time.',
    heroTags: ['Real-time queue visibility', 'Counter-level monitoring'],
    primaryKpiLabel: 'Total in queue',
    showAppointmentsRail: false,
    showOffersRail: true
  }
};

const safeNumber = (value, fallback = 0) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
};

const resolveStatusTone = (statusLabel) => {
  const normalized = String(statusLabel || '').trim().toLowerCase();
  return normalized === 'offline' ? 'offline' : 'active';
};

const formatFloorLabel = (value) => {
  const floor = safeNumber(value, 1);
  return floor <= 1 ? 'Ground Floor' : `Floor ${floor}`;
};

const formatZoneLabel = (value) => {
  const zone = String(value || '').trim();
  return zone || 'Unassigned zone';
};

const normalizeCounterLabel = (value) => String(value || '')
  .replace(/\s+/g, ' ')
  .trim()
  .toLowerCase();

const formatTimeLabel = (value) => {
  if (!value) return 'Updated moments ago';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Updated moments ago';
  return date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
};

const formatJoinedLabel = (value) => {
  if (!value) return 'Joined recently';
  const joinedAt = new Date(value);
  if (Number.isNaN(joinedAt.getTime())) return 'Joined recently';
  const minutes = Math.max(0, Math.round((Date.now() - joinedAt.getTime()) / 60000));
  return `Joined ${minutes} min ago`;
};

const toTitleCase = (value) => String(value || '')
  .trim()
  .toLowerCase()
  .split(/\s+/)
  .filter(Boolean)
  .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
  .join(' ');

const normalizeQueueStatus = (rawStatus) => {
  const status = String(rawStatus || '').trim().toLowerCase();
  if (status.includes('served') || status.includes('done') || status.includes('complete')) {
    return { label: 'Served', tone: 'served' };
  }
  if (status.includes('called') || status.includes('notified') || status.includes('in_progress')) {
    return { label: 'Called', tone: 'called' };
  }
  if (status.includes('skip')) {
    return { label: 'Skipped', tone: 'skipped' };
  }
  return { label: 'Waiting', tone: 'waiting' };
};

const extractCustomerRows = (stats, sectionUuid, counters = []) => {
  const rowFeed = Array.isArray(stats?.queueRows) ? stats.queueRows : [];
  const counterByUuid = new Map(
    counters
      .filter((counter) => counter?.id)
      .map((counter) => [String(counter.id), counter])
  );

  return rowFeed.slice(0, 20).map((row, rowIndex) => {
    const waited = Math.max(0, safeNumber(
      row?.waitedMins ?? row?.waitMinutes ?? row?.waitTimeMins ?? row?.elapsedMins, 0
    ));
    const resolvedCounterName = String(
      row?.counterName
      || row?.counterLabel
      || row?.counter
      || counterByUuid.get(String(row?.counterUuid || ''))?.name
      || '--'
    );
    const normalizedStatus = normalizeQueueStatus(row?.status || row?.queueStatus || row?.state);
    const customerPhone = String(row?.customerPhone || row?.phone || row?.mobile || '').trim();
    return {
      id: row?.queueUuid || row?.tokenUuid || row?.queueToken || `${sectionUuid}-row-${rowIndex}`,
      token: String(
        row?.token
        || row?.tokenNo
        || row?.tokenNumber
        || row?.queueToken
        || safeNumber(row?.position ?? row?.queuePosition, rowIndex + 1)
      ),
      customerName: String(row?.customerName || row?.name || 'Customer'),
      customerPhone,
      counterUuid: String(row?.counterUuid || ''),
      counterName: resolvedCounterName,
      otp: String(row?.otp || row?.verificationCode || '--'),
      waited,
      joinedLabel: customerPhone || formatJoinedLabel(row?.joinedAt || row?.createdAt || row?.queuedAt),
      statusLabel: normalizedStatus.label,
      statusTone: normalizedStatus.tone,
      // W3 — offer attached to this walk-in ("I saw this offer" flow).
      queueUuid: row?.queueUuid || null,
      offerId: row?.offerId ?? null,
      offerTitle: row?.offerTitle || null,
      offerCode: row?.offerCode || null
    };
  });
};

const getSortValue = (row, key) => {
  if (key === 'position' || key === 'waited') return safeNumber(row?.[key], 0);
  if (key === 'token') return String(row?.token || '').toLowerCase();
  if (key === 'otp') {
    const otpValue = String(row?.otp || '').trim();
    const numericOtp = Number(otpValue);
    return Number.isFinite(numericOtp) ? numericOtp : otpValue.toLowerCase();
  }
  return String(row?.[key] || '').toLowerCase();
};

const sortQueueRows = (rows = [], sortConfig = DEFAULT_SORT_CONFIG) => {
  if (!Array.isArray(rows) || !sortConfig?.key) return Array.isArray(rows) ? rows : [];
  const directionFactor = sortConfig.direction === 'desc' ? -1 : 1;
  return rows
    .map((row, index) => ({ row, index }))
    .sort((left, right) => {
      const leftValue = getSortValue(left.row, sortConfig.key);
      const rightValue = getSortValue(right.row, sortConfig.key);
      let comparison = 0;
      if (typeof leftValue === 'number' && typeof rightValue === 'number') {
        comparison = leftValue - rightValue;
      } else {
        comparison = String(leftValue).localeCompare(String(rightValue), undefined, { numeric: true, sensitivity: 'base' });
      }
      if (comparison === 0) return left.index - right.index;
      return comparison * directionFactor;
    })
    .map((item) => item.row);
};

const normalizeCounterCards = (sectionStats = {}) => {
  const counters = Array.isArray(sectionStats?.counters) ? sectionStats.counters : [];
  return counters.map((counter, index) => {
    const active = Boolean(counter?.active);
    return {
      id: String(counter?.counterUuid || `${sectionStats?.sectionUuid || 'section'}-counter-${index + 1}`),
      name: String(counter?.counterName || `Counter ${index + 1}`),
      queueCount: Math.max(0, safeNumber(counter?.inQueueNow, 0)),
      estimatedWait: Math.max(0, safeNumber(counter?.estimatedWaitMins, 0)),
      active,
      tone: String(counter?.tone || (active ? 'good' : 'muted')).toLowerCase()
    };
  });
};

const buildSectionCards = (liveStats) => {
  const liveSections = Array.isArray(liveStats?.sections) ? liveStats.sections : [];
  return liveSections
    .map((section) => {
      const counterCards = normalizeCounterCards(section);
      const customerRows = extractCustomerRows(section, section?.sectionUuid, counterCards);
      return {
        sectionUuid: String(section?.sectionUuid || ''),
        sectionName: String(section?.sectionName || 'Section'),
        zoneLabel: formatZoneLabel(section?.zoneName || section?.sectionWing || section?.zone),
        badgeLabel: (SECTION_BADGES[section?.sectionType] || { label: 'SECTION' }).label,
        badgeTone: (SECTION_BADGES[section?.sectionType] || { tone: 'default' }).tone,
        floorLabel: String(section?.floorLabel || formatFloorLabel(section?.floorNumber)),
        totalCounters: Math.max(0, safeNumber(section?.countersTotal, 0)),
        activeCounterCount: Math.max(0, safeNumber(section?.countersUp, 0)),
        downCounterCount: Math.max(0, safeNumber(section?.countersDown, 0)),
        inQueueNow: Math.max(0, safeNumber(section?.inQueueNow, 0)),
        statusLabel: String(section?.statusLabel || 'Active'),
        statusTone: resolveStatusTone(section?.statusLabel),
        counterCards,
        customerRows
      };
    })
    .filter((section) => Boolean(section.sectionUuid));
};

const buildLiveModel = (liveStats) => {
  const sectionCards = buildSectionCards(liveStats);
  const totalInQueue = Math.max(0, safeNumber(liveStats?.totalInQueueNow, 0));
  const activeCounters = Math.max(0, safeNumber(liveStats?.activeCounters, 0));
  const downCounters = Math.max(0, safeNumber(liveStats?.offlineCounters, 0));
  const totalCounters = Math.max(0, activeCounters + downCounters);
  const liveSections = Math.max(0, safeNumber(liveStats?.liveSections, sectionCards.length));
  const loadPerActiveCounter = Math.max(0, safeNumber(liveStats?.loadPerActiveCounter, 0));
  const longestQueue = Math.max(0, safeNumber(liveStats?.busiestSectionQueue, 0));
  const actions = Array.isArray(liveStats?.recommendedActions)
    ? liveStats.recommendedActions.filter(Boolean).map((item) => String(item))
    : [];
  const watchlist = Array.isArray(liveStats?.priorityWatchlist)
    ? liveStats.priorityWatchlist.filter(Boolean).map((item) => String(item))
    : [];
  const volumeBars = sectionCards.map((section) => ({
    id: section.sectionUuid || section.sectionName || '--',
    fullLabel: String(section.sectionName || '--').replace(/\s+/g, ' ').trim(),
    zoneLabel: String(section.zoneLabel || 'Unassigned zone'),
    floorLabel: String(section.floorLabel || 'Ground Floor'),
    label: String(section.sectionName || '--').replace(/\s+/g, ' ').trim().split(' ').slice(0, 2).join(' '),
    value: Math.max(0, safeNumber(section.inQueueNow, 0))
  }));
  const peakVolume = volumeBars.length > 0 ? Math.max(...volumeBars.map((bar) => bar.value)) : 0;
  const peakBar = volumeBars.find((bar) => bar.value === peakVolume) || {
    id: '--', label: '--', fullLabel: '--', zoneLabel: 'Unassigned zone', floorLabel: 'Ground Floor', value: 1
  };
  return {
    totalInQueue, activeCounters, downCounters, totalCounters, liveSections,
    loadPerActiveCounter, longestQueue, sectionCards, watchlist, actions,
    volumeBars, peakBar, updatedAtLabel: formatTimeLabel(liveStats?.generatedAt)
  };
};

const LiveViewLoadingSkeleton = () => (
  <section className="live-v2-loading-skeleton" aria-label="Loading live operations">
    <div className="live-v2-kpi-strip">
      {Array.from({ length: 4 }).map((_, index) => (
        <div className="live-v2-kpi-card" key={`live-kpi-skeleton-${index}`}>
          <Skeleton height={12} width="55%" />
          <Skeleton height={30} width="45%" />
          <Skeleton height={12} width="70%" />
        </div>
      ))}
    </div>
    <div className="live-v2-body-grid">
      <div className="live-v2-sections-column">
        <section className="live-v2-section-panel">
          <Skeleton height={18} width={220} />
          <Skeleton height={14} width="50%" />
          <Skeleton height={38} width="100%" />
          <Skeleton height={38} width="100%" />
          <Skeleton height={38} width="100%" />
        </section>
      </div>
      <aside className="live-v2-right-rail">
        <section className="live-v2-rail-card">
          <Skeleton height={18} width={160} />
          <Skeleton height={34} width={100} />
          <Skeleton height={12} width="75%" />
        </section>
      </aside>
    </div>
  </section>
);

const StoreLiveView = ({
  liveStats,
  modules,
  clientType,
  appointmentsTodayCount,
  activeOffersCount,
  onSelectTab,
  isLiveRefreshing = false,
  liveLastFetchedAt = null,
  onRefreshLive,
  request,
  onToast
}) => {
  const [sortBySection, setSortBySection] = useState({});
  const [selectedCounterBySection, setSelectedCounterBySection] = useState({});
  const [queueFilter, setQueueFilter] = useState('all');
  // W3 — walk-in offer attach ("I saw this offer in the app").
  const [offerPickerRow, setOfferPickerRow] = useState(null);
  const [pickerOffers, setPickerOffers] = useState([]);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [pickerSearch, setPickerSearch] = useState('');
  const [isOfferMutating, setIsOfferMutating] = useState(false);

  const openOfferPicker = useCallback(async (row) => {
    if (!row?.queueUuid) {
      return;
    }
    setOfferPickerRow(row);
    setPickerSearch('');
    setPickerOffers([]);
    setPickerLoading(true);
    try {
      // W3+ — per-customer candidates: every live offer comes back with an
      // eligibility verdict for THIS walk-in (audience, caps, bucket).
      const response = await request(
        `${PROFILE_STORES_OFFERS}/walkins/queue/${row.queueUuid}/offers`,
        {
          method: 'GET',
          requestName: 'walkin-offer-picker-list'
        }
      );
      // request() already returns the parsed ApiResponse envelope, so
      // response.data IS the candidates array.
      const payload = response?.data;
      const list = Array.isArray(payload) ? payload : Array.isArray(payload?.data) ? payload.data : [];
      setPickerOffers([...list].sort(
        (a, b) => Number(b?.eligible === true) - Number(a?.eligible === true)
      ));
    } catch (error) {
      onToast?.({ message: 'Could not load offers. Try again.', variant: 'error' });
      setOfferPickerRow(null);
    } finally {
      setPickerLoading(false);
    }
  }, [request, onToast]);

  const attachOfferToWalkin = useCallback(async (offer) => {
    if (!offerPickerRow?.queueUuid || !offer?.id) {
      return;
    }
    setIsOfferMutating(true);
    try {
      await request(`${PROFILE_STORES_OFFERS}/walkins/queue/${offerPickerRow.queueUuid}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        requestName: 'walkin-offer-attach',
        body: JSON.stringify({ offerId: offer.id })
      });
      onToast?.({ message: `Offer attached to ${offerPickerRow.customerName}`, variant: 'success' });
      setOfferPickerRow(null);
      onRefreshLive?.();
    } catch (error) {
      const message = error?.data?.message || error?.message || 'Could not attach the offer.';
      onToast?.({ message, variant: 'error' });
    } finally {
      setIsOfferMutating(false);
    }
  }, [offerPickerRow, request, onToast, onRefreshLive]);

  const detachOfferFromWalkin = useCallback(async (row) => {
    if (!row?.queueUuid) {
      return;
    }
    setIsOfferMutating(true);
    try {
      await request(`${PROFILE_STORES_OFFERS}/walkins/queue/${row.queueUuid}`, {
        method: 'DELETE',
        requestName: 'walkin-offer-detach'
      });
      onToast?.({ message: 'Offer removed — claim released back to the customer', variant: 'success' });
      onRefreshLive?.();
    } catch (error) {
      const message = error?.data?.message || error?.message || 'Could not remove the offer.';
      onToast?.({ message, variant: 'error' });
    } finally {
      setIsOfferMutating(false);
    }
  }, [request, onToast, onRefreshLive]);

  const visiblePickerOffers = useMemo(() => {
    const query = pickerSearch.trim().toLowerCase();
    if (!query) {
      return pickerOffers;
    }
    return pickerOffers.filter((offer) => (
      String(offer?.title || '').toLowerCase().includes(query)
      || String(offer?.couponCode || '').toLowerCase().includes(query)
    ));
  }, [pickerOffers, pickerSearch]);

  const vertical = useMemo(() => detectVertical(clientType, modules), [clientType, modules]);
  const config = VERTICAL_CONFIG[vertical] || VERTICAL_CONFIG.retail;

  const liveModel = useMemo(() => buildLiveModel(liveStats), [liveStats]);

  const nowServing = useMemo(() => {
    for (const section of liveModel.sectionCards) {
      const calledRow = section.customerRows.find((r) => r.statusTone === 'called');
      if (calledRow) return { ...calledRow, sectionName: section.sectionName };
    }
    return null;
  }, [liveModel.sectionCards]);

  const handleCounterSelect = useCallback((sectionUuid, counterName) => {
    if (!sectionUuid) return;
    const normalized = normalizeCounterLabel(counterName);
    if (!normalized) return;
    setSelectedCounterBySection((prev) => {
      const current = normalizeCounterLabel(prev[sectionUuid]);
      if (current === normalized) return { ...prev, [sectionUuid]: '' };
      return { ...prev, [sectionUuid]: counterName };
    });
  }, []);

  const handleResetCounterFilter = useCallback((sectionUuid) => {
    if (!sectionUuid) return;
    setSelectedCounterBySection((prev) => ({ ...prev, [sectionUuid]: '' }));
  }, []);

  const toggleSectionSort = useCallback((sectionUuid, columnKey) => {
    if (!sectionUuid || !columnKey) return;
    setSortBySection((prev) => {
      const currentSort = prev[sectionUuid] || DEFAULT_SORT_CONFIG;
      if (currentSort.key !== columnKey) return { ...prev, [sectionUuid]: { key: columnKey, direction: 'asc' } };
      if (currentSort.direction === 'asc') return { ...prev, [sectionUuid]: { key: columnKey, direction: 'desc' } };
      return { ...prev, [sectionUuid]: DEFAULT_SORT_CONFIG };
    });
  }, []);

  // Primary KPI value: appointments-first for clinic/salon, queue for retail
  const primaryKpiValue = (vertical === 'clinic' || vertical === 'salon')
    ? safeNumber(appointmentsTodayCount, 0)
    : liveModel.totalInQueue;
  const showInitialLoading = isLiveRefreshing && !liveStats;

  const QUEUE_FILTERS = [
    { key: 'all', label: 'All' },
    { key: 'waiting', label: 'Waiting' },
    { key: 'called', label: 'Called' },
    { key: 'served', label: 'Served' }
  ];

  const filterRows = useCallback((rows) => {
    if (queueFilter === 'all') return rows;
    return rows.filter((row) => row.statusTone === queueFilter);
  }, [queueFilter]);

  return (
    <div className="live-view-container">
      {showInitialLoading ? (
        <LiveViewLoadingSkeleton />
      ) : (
        <>
      {/* KPI strip — 4 cards */}
      <div className="live-v2-kpi-strip">
        <div className="live-v2-kpi-card">
          <div className="live-v2-kpi-label">{config.primaryKpiLabel}</div>
          <div className="live-v2-kpi-value highlight-green">{primaryKpiValue}</div>
          <div className="live-v2-kpi-meta">Updated {liveModel.updatedAtLabel}</div>
        </div>
        <div className="live-v2-kpi-card">
          <div className="live-v2-kpi-label">Active counters</div>
          <div className="live-v2-kpi-value">{liveModel.activeCounters}</div>
          <div className="live-v2-kpi-meta">Out of {liveModel.totalCounters} total</div>
        </div>
        <div className="live-v2-kpi-card">
          <div className="live-v2-kpi-label">Live sections</div>
          <div className="live-v2-kpi-value">{liveModel.liveSections}</div>
          <div className="live-v2-kpi-meta">Sections currently tracked</div>
        </div>
        <div className="live-v2-kpi-card">
          <div className="live-v2-kpi-label">Avg wait time</div>
          <div className="live-v2-kpi-value highlight-green">
            {liveModel.loadPerActiveCounter}<span className="live-v2-kpi-unit">min</span>
          </div>
          <div className="live-v2-kpi-meta">Queue pressure indicator</div>
        </div>
      </div>

      <div className="live-v2-body-grid">
        {/* Main column: queue sections */}
        <div className="live-v2-sections-column">
          {/* Clinic / Salon: show appointment prompt above sections when no queue sections exist */}
          {(vertical === 'clinic' || vertical === 'salon') && liveModel.sectionCards.length === 0 && (
            <div className="live-v2-vertical-prompt">
              <p className="live-v2-vertical-prompt-title">Appointments-first view</p>
              <p className="live-v2-vertical-prompt-body">
                You have <strong>{safeNumber(appointmentsTodayCount, 0)}</strong> appointment{safeNumber(appointmentsTodayCount, 0) !== 1 ? 's' : ''} scheduled today.
                Go to the Appointments tab to manage bookings, mark arrivals, and update statuses.
              </p>
              {onSelectTab ? (
                <button
                  type="button"
                  className="btn primary sm"
                  onClick={() => onSelectTab('appointments')}
                >
                  Open Appointments
                </button>
              ) : null}
            </div>
          )}

          {liveModel.sectionCards.map((section) => {
            const sectionSortConfig = sortBySection[section.sectionUuid] || DEFAULT_SORT_CONFIG;
            const selectedCounterName = selectedCounterBySection[section.sectionUuid] || '';
            const normalizedSelectedCounter = normalizeCounterLabel(selectedCounterName);
            const hasCounterFilter = Boolean(normalizedSelectedCounter);
            const counterFilteredRows = normalizedSelectedCounter
              ? section.customerRows.filter((row) => normalizeCounterLabel(row.counterName) === normalizedSelectedCounter)
              : section.customerRows;
            const filteredRows = filterRows(counterFilteredRows);
            const sortedRows = sortQueueRows(filteredRows, sectionSortConfig);

            return (
              <section key={section.sectionUuid} className="live-v2-section-panel">
                <header className="live-v2-section-top">
                  <div className="live-v2-section-heading">
                    <span className={`live-v2-section-tag ${section.badgeTone}`}>{section.badgeLabel}</span>
                    <div>
                      <h4>{section.sectionName}</h4>
                      <p>{section.totalCounters} counters · {section.floorLabel}</p>
                    </div>
                  </div>
                  <div className="live-v2-section-actions">
                    <span className={`live-v2-status-pill ${section.statusTone}`}>{section.statusLabel}</span>
                  </div>
                </header>

                <div className="live-v2-counter-grid">
                  {section.counterCards.map((counter) => (
                    <article
                      key={counter.id}
                      className={`live-v2-counter-card ${counter.tone} ${normalizeCounterLabel(counter.name) === normalizedSelectedCounter ? 'is-selected' : ''}`}
                      role="button"
                      tabIndex={0}
                      onClick={() => handleCounterSelect(section.sectionUuid, counter.name)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          handleCounterSelect(section.sectionUuid, counter.name);
                        }
                      }}
                      aria-label={`Filter customers for ${counter.name}`}
                    >
                      <div className="live-v2-counter-head">
                        <span className="live-v2-counter-name">{counter.name}</span>
                        <span className={`live-v2-counter-dot ${counter.tone}`} />
                      </div>
                      <div className="live-v2-counter-queue">{counter.active ? counter.queueCount : '—'}</div>
                      <div className="live-v2-counter-copy">{counter.active ? 'in queue' : 'offline'}</div>
                      <div className="live-v2-counter-wait">
                        {!counter.active ? 'Unavailable' : counter.queueCount === 0 ? 'Free now' : `~${counter.estimatedWait} min`}
                      </div>
                    </article>
                  ))}
                </div>

                <div className="live-v2-queue-table">
                  <div className="live-v2-queue-panel-head">
                    <span className="live-v2-queue-panel-title">Queue</span>
                    <div className="live-v2-queue-filter-tabs">
                      {QUEUE_FILTERS.map((f) => (
                        <button
                          key={f.key}
                          type="button"
                          className={`live-v2-queue-filter-tab ${queueFilter === f.key ? 'active' : ''}`}
                          onClick={() => setQueueFilter(f.key)}
                        >
                          {f.label}
                        </button>
                      ))}
                    </div>
                    <button type="button" className="btn primary sm">+ Walk-in</button>
                  </div>
                  {hasCounterFilter && (
                    <div className="live-v2-counter-filter-bar">
                      <span>Showing customers for: <strong>{selectedCounterName}</strong></span>
                      <button
                        type="button"
                        className="live-v2-inline-btn"
                        onClick={() => handleResetCounterFilter(section.sectionUuid)}
                      >
                        Go to default view
                      </button>
                    </div>
                  )}
                  <div className="live-v2-queue-head">
                    {QUEUE_SORT_COLUMNS.map((column) => {
                      const isActive = sectionSortConfig.key === column.key;
                      const sortState = !isActive ? 'none' : sectionSortConfig.direction;
                      return (
                        <button
                          key={`${section.sectionUuid}-${column.key}`}
                          type="button"
                          className={`live-v2-queue-sort-btn ${isActive ? 'active' : ''} ${sortState}`}
                          onClick={() => toggleSectionSort(section.sectionUuid, column.key)}
                          aria-label={`Sort ${column.label} ${isActive ? sectionSortConfig.direction : 'none'}`}
                        >
                          <span>{column.label}</span>
                          <span className="live-v2-queue-sort-indicator" aria-hidden="true" />
                        </button>
                      );
                    })}
                    <span className="live-v2-queue-head-actions-label">Actions</span>
                  </div>

                  {sortedRows.length > 0 ? sortedRows.map((row) => (
                    <div key={row.id} className="live-v2-queue-row">
                      <span className="live-v2-queue-token billing-tabular">{row.token}</span>
                      <div className="live-v2-queue-customer">
                        <strong>{row.customerName}</strong>
                        <small>{row.joinedLabel}</small>
                        {row.offerId != null && (
                          <span className="live-v2-offer-chip" title="Offer attached to this walk-in">
                            <Tag size={10} aria-hidden="true" />
                            <span>{row.offerTitle || row.offerCode || 'Offer'}</span>
                          </span>
                        )}
                      </div>
                      <span className={`live-v2-queue-wait ${row.waited >= WAIT_RED_THRESHOLD_MIN ? 'critical' : row.waited >= WAIT_AMBER_THRESHOLD_MIN ? 'warning' : ''}`}>
                        <Clock size={14} aria-hidden="true" /> {row.waited} min
                      </span>
                      <span className={`live-v2-queue-status ${row.statusTone}`}>
                        {toTitleCase(row.statusLabel)}
                      </span>
                      <div className="live-v2-queue-actions">
                        <button type="button" className="live-v2-action-icon-btn" title="Call customer" aria-label={`Call ${row.customerName}`}>
                          <Play size={14} aria-hidden="true" />
                          <span className="live-v2-action-icon-btn-label">Call</span>
                        </button>
                        <button type="button" className="live-v2-action-icon-btn" title="Mark served" aria-label={`Mark ${row.customerName} served`}>
                          <Check size={14} aria-hidden="true" />
                          <span className="live-v2-action-icon-btn-label">Served</span>
                        </button>
                        <button type="button" className="live-v2-action-icon-btn" title="Skip customer" aria-label={`Skip ${row.customerName}`}>
                          <SkipForward size={14} aria-hidden="true" />
                          <span className="live-v2-action-icon-btn-label">Skip</span>
                        </button>
                        {row.queueUuid && (
                          <button
                            type="button"
                            className={`live-v2-action-icon-btn ${row.offerId != null ? 'live-v2-action-offer-on' : ''}`}
                            title={row.offerId != null ? 'Remove attached offer' : 'Attach offer'}
                            aria-label={row.offerId != null
                              ? `Remove offer from ${row.customerName}`
                              : `Attach offer to ${row.customerName}`}
                            disabled={isOfferMutating}
                            onClick={() => (row.offerId != null
                              ? detachOfferFromWalkin(row)
                              : openOfferPicker(row))}
                          >
                            <Tag size={14} aria-hidden="true" />
                            <span className="live-v2-action-icon-btn-label">
                              {row.offerId != null ? 'Remove' : 'Offer'}
                            </span>
                          </button>
                        )}
                      </div>
                    </div>
                  )) : (
                    <div className="live-v2-queue-empty">
                      {normalizedSelectedCounter
                        ? `No customers currently mapped to ${selectedCounterName}.`
                        : 'No customers are currently waiting in this section.'}
                    </div>
                  )}
                </div>
              </section>
            );
          })}

          {liveModel.sectionCards.length === 0 && vertical === 'retail' && (
            <div className="live-v2-empty-board">
              Add sections in Manage to activate live operations view.
            </div>
          )}
        </div>

        {/* Right rail — Now Serving action panel */}
        <aside className="live-v2-right-rail">
          <section className="live-v2-action-panel">
            <h4 className="live-v2-action-panel-title">Now serving</h4>
            {nowServing ? (
              <>
                <div className="live-v2-action-panel-token">{nowServing.token}</div>
                <div className="live-v2-action-panel-meta">
                  <span className="live-v2-action-panel-name">{nowServing.customerName}</span>
                  <span className="live-v2-action-panel-sub">{nowServing.sectionName} · {nowServing.waited} min wait</span>
                </div>
                <div className="live-v2-action-panel-buttons">
                  <button type="button" className="live-v2-action-btn primary">
                    <Check size={16} aria-hidden="true" /> Mark complete · call next
                  </button>
                  <button type="button" className="live-v2-action-btn secondary">
                    <Pause size={16} aria-hidden="true" /> Pause queue
                  </button>
                  <button type="button" className="live-v2-action-btn danger">
                    <X size={16} aria-hidden="true" /> Cancel customer
                  </button>
                </div>
              </>
            ) : (
              <div className="live-v2-action-panel-empty">
                <p>No customer is currently being served.</p>
                <p className="live-v2-action-panel-hint">Call a customer from the queue to start serving.</p>
              </div>
            )}

            <div className="live-v2-action-panel-settings">
              <div className="live-v2-action-panel-setting-row">
                <span>Auto-call next</span>
                <span className="live-v2-action-panel-setting-value green">On</span>
              </div>
              <div className="live-v2-action-panel-setting-row">
                <span>OTP verification</span>
                <span className="live-v2-action-panel-setting-value">Off</span>
              </div>
              <div className="live-v2-action-panel-setting-row">
                <span>Queue capacity</span>
                <span className="live-v2-action-panel-setting-value">{liveModel.totalInQueue} / ∞</span>
              </div>
            </div>
          </section>

          {/* Summary cards */}
          <section className="live-v2-rail-card">
            <h4>Today at a glance</h4>
            <div className="live-v2-glance-grid">
              <div><strong>{liveModel.totalInQueue}</strong><span>Queue now</span></div>
              <div><strong>{liveModel.activeCounters}</strong><span>Active counters</span></div>
              <div><strong>{liveModel.downCounters}</strong><span>Offline counters</span></div>
              <div><strong>{liveModel.longestQueue}</strong><span>Busiest section queue</span></div>
            </div>
          </section>

          {config.showAppointmentsRail && (
            <section className="live-v2-rail-card live-v2-rail-card-accent">
              <div className="live-v2-rail-title-row">
                <h4>Appointments today</h4>
                {onSelectTab ? (
                  <button type="button" className="live-v2-inline-btn" onClick={() => onSelectTab('appointments')}>View all</button>
                ) : null}
              </div>
              <p className="live-v2-rail-big-number">{safeNumber(appointmentsTodayCount, 0)}</p>
              <p className="live-v2-rail-sublabel">bookings scheduled for today</p>
            </section>
          )}

          {liveModel.actions.length > 0 && (
            <section className="live-v2-rail-card">
              <div className="live-v2-rail-title-row">
                <h4>Recommended actions</h4>
                <span className="live-v2-count-pill">{liveModel.actions.length}</span>
              </div>
              <ul className="live-v2-list">
                {liveModel.actions.map((item) => <li key={item}>{item}</li>)}
              </ul>
            </section>
          )}
        </aside>
      </div>
        </>
      )}

      {/* W3 — walk-in offer picker ("I saw this offer in the app") */}
      {offerPickerRow && (
        <div
          className="zone-modal-overlay"
          role="presentation"
          onClick={(event) => {
            if (event.target === event.currentTarget) {
              setOfferPickerRow(null);
            }
          }}
        >
          <div
            className="zone-modal live-v2-offer-picker-modal"
            role="dialog"
            aria-modal="true"
            aria-label={`Attach offer to ${offerPickerRow.customerName}`}
          >
            <div className="zone-modal-header">
              <div>
                <h3>Attach offer</h3>
                <p>{`Walk-in · ${offerPickerRow.customerName}`}</p>
              </div>
              <button type="button" className="btn ghost" onClick={() => setOfferPickerRow(null)}>
                <X size={15} aria-hidden="true" /> Close
              </button>
            </div>
            <div className="live-v2-offer-picker-body">
              <input
                className="live-v2-offer-picker-search"
                type="search"
                placeholder="Search live offers by title or coupon code"
                value={pickerSearch}
                onChange={(event) => setPickerSearch(event.target.value)}
                autoFocus
              />
              {pickerLoading ? (
                <div className="live-v2-offer-picker-loading">
                  <Skeleton height={44} width="100%" />
                  <Skeleton height={44} width="100%" />
                </div>
              ) : visiblePickerOffers.length === 0 ? (
                <p className="live-v2-offer-picker-empty">
                  {pickerOffers.length === 0
                    ? 'No live offers right now — publish one from the Offers tab first.'
                    : 'No live offers match this search.'}
                </p>
              ) : (
                <ul className="live-v2-offer-picker-list">
                  {visiblePickerOffers.map((offer) => (
                    <li key={offer.id}>
                      <button
                        type="button"
                        className={`live-v2-offer-picker-item${offer.eligible === false ? ' ineligible' : ''}`}
                        disabled={isOfferMutating || offer.eligible === false}
                        title={offer.eligible === false ? (offer.reason || 'Not eligible for this customer') : undefined}
                        onClick={() => attachOfferToWalkin(offer)}
                      >
                        <span className="live-v2-offer-picker-item-main">
                          <strong>{offer.title || 'Untitled offer'}</strong>
                          <small>
                            {offer.discount || ''}
                            {offer.couponCode ? ` · ${offer.couponCode}` : ''}
                          </small>
                          {offer.eligible === false && offer.reason && (
                            <small className="live-v2-offer-picker-item-reason">{offer.reason}</small>
                          )}
                        </span>
                        <span className={`live-v2-offer-picker-chip ${offer.eligible === false ? 'ineligible' : 'eligible'}`}>
                          {offer.eligible === false ? 'Not eligible' : 'Eligible'}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StoreLiveView;
